import React, { useMemo } from 'react'
import { Group, SecondaryGroup } from '@/model/types/goods'
import { sortGoodStore } from '../model'
import { observer } from 'mobx-react'

import './sort-goods-group.scss'
import useBemClass from '@/utils/hooks/useBEMClass'
import { SortableElement, SortableContainer } from 'react-sortable-hoc'
import { useDynamicList, useRequest } from 'ahooks'
import { useRef } from 'react'
import { $post } from '@/api'
import $msg from '@/utils/message'
import { errorCallBack } from '@/utils'

interface SelectGoodsGroupItemProps {
    group: Group
    groupArr: Group[]
    groupIndex: number
    secondGroupId: number
    onClick: (v: Group[], groupIndex: number) => void
}

const SortSecondGroupItem = SortableElement(
    (props: { secondId: number; groupId: number | undefined; name: string; active: boolean }) => {
        const getCls = useBemClass('sort-goods-group')

        return (
            <div
                className={`${props.active ? getCls('menu', 'active') : getCls('menu')}`}
                onClick={() => {
                    sortGoodStore.setActiveId(props.groupId)
                    sortGoodStore.setSecondId(props.secondId)
                }}
            >
                {props.name}
            </div>
        )
    },
)

const SortSecondGroupList = SortableContainer(
    (props: {
        open: boolean
        secondary_group: SecondaryGroup[]
        groupId: number | undefined
        activeSecondId: number
    }) => {
        const getCls = useBemClass('sort-goods-group')

        return (
            <div
                className={getCls('item', 'menu')}
                style={{
                    height: props.open ? `${props.secondary_group.length * 70}px` : '0',
                }}
            >
                {props.secondary_group.map((second, index) => (
                    <SortSecondGroupItem
                        index={index}
                        active={second.id === props.activeSecondId}
                        key={second.id}
                        secondId={second.id}
                        groupId={props.groupId}
                        name={second.name}
                    />
                ))}
            </div>
        )
    },
)

const SelectGoodsGroupItem: React.FC<SelectGoodsGroupItemProps> = observer(props => {
    const getCls = useBemClass('sort-goods-group')

    const { group } = props

    const { list: secondaryGroup, move } = useDynamicList<SecondaryGroup>(group.secondary_group)
    const allSecondIds = useRef<number[]>(group.secondary_group.map(g => g.id))

    const isActive = useMemo(
        () =>
            group.id === sortGoodStore.activeId ||
            (!!group.secondary_group.length && group.secondary_group.every(i => i.id === sortGoodStore.secondId)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [group, sortGoodStore.activeId, sortGoodStore.secondId],
    )

    const sortGroupReq = useRequest(
        () =>
            $post({
                url: '/goods/editgrouppriority',
                params: { secondary_group_ids: allSecondIds.current },
            }),
        {
            manual: true,
            onSuccess: () => {
                $msg('success', '分组排序成功')
                sortGoodStore.setGroupUpdate()
            },
            onError: errorCallBack,
        },
    )

    return (
        <div>
            <div
                className={`${
                    isActive
                        ? props.group.secondary_group.length
                            ? getCls('item', 'active-second')
                            : getCls('item', 'active')
                        : getCls('item')
                }`}
                onClick={() => {
                    props.onClick(props.groupArr, props.groupIndex)
                    if (!props.group.secondary_group.length) {
                        sortGoodStore.setActiveId(group.id)
                        sortGoodStore.setSecondId(0) // 初始化一下,防止在无二级分组的情况下请求二级分组
                    }
                }}
            >
                {!!group.secondary_group.length && !!group.secondary_group[0].id && (
                    <i
                        className={`icon6xiala2-20 iconfont ${getCls('arrow')}`}
                        style={{
                            transform: group.open ? 'rotate(0)' : 'rotate(-90deg)',
                        }}
                    />
                )}
                <div className={getCls('overflow')}>{group.name}</div>
            </div>
            <SortSecondGroupList
                axis="y"
                distance={10}
                helperClass="paymode-sort-helper"
                activeSecondId={sortGoodStore.secondId}
                secondary_group={secondaryGroup}
                open={!!(group.open && group.secondary_group[0]?.id)}
                groupId={group.id}
                onSortEnd={({ oldIndex, newIndex }) => {
                    if (oldIndex === newIndex) return
                    move(oldIndex, newIndex)
                    ;[allSecondIds.current[oldIndex], allSecondIds.current[newIndex]] = [
                        allSecondIds.current[newIndex],
                        allSecondIds.current[oldIndex],
                    ]
                    sortGroupReq.run()
                }}
            />
        </div>
    )
})

export const SortSelectGoodGroupItem = SortableElement(SelectGoodsGroupItem)

export default SelectGoodsGroupItem
